export type StringOperator = 'contains' | 'eq' | 'neq' | 'notContain';
export type NumericOperator = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte';
export type EnumOperator = 'eq' | 'neq';

export function buildAndFilter<T>(clauses: T[]): { and?: T[] } {
  return clauses.length > 0 ? { and: clauses } : {};
}

export function buildOrFilter<T>(clauses: T[]): { or?: T[] } {
  return clauses.length > 0 ? { or: clauses } : {};
}

export const ORDERS_FILTER_DATA_KEY = 'orderFilters';
export const COMPARISON_FILTER_DATA_KEY = 'comparisonFilters';
export const PRICING_FILTER_DATA_KEY = 'pricingFilters';
export const REQUEST_FOR_QUOTE_FILTER_DATA_KEY = 'requestForQuoteFilters';

export function hasOwnKey(filterData: unknown, key: string): boolean {
  return (
    typeof filterData === 'object' &&
    filterData !== null &&
    !Array.isArray(filterData) &&
    Object.hasOwn(filterData, key)
  );
}

/**
 * An Orders saved view is one whose stored filter data carries the orderFilters
 * key. Every other shape — legacy, unknown, or empty — belongs to the inventory
 * and product pages, so nothing that exists today moves.
 */
export function isOrdersFilterData(filterData: unknown): boolean {
  return hasOwnKey(filterData, ORDERS_FILTER_DATA_KEY);
}

export function isComparisonFilterData(filterData: unknown): boolean {
  return hasOwnKey(filterData, COMPARISON_FILTER_DATA_KEY);
}

export function isPricingFilterData(filterData: unknown): boolean {
  return hasOwnKey(filterData, PRICING_FILTER_DATA_KEY);
}

export function isRequestForQuoteFilterData(filterData: unknown): boolean {
  return hasOwnKey(filterData, REQUEST_FOR_QUOTE_FILTER_DATA_KEY);
}

export type FilterConnector = 'and' | 'or';

/**
 * Splits rows into groups joined by OR. Consecutive rows connected by `and`
 * stay in one group; a row whose connector is `or` starts a new one.
 */
export function groupRows<T extends { connector: FilterConnector }>(
  rows: T[]
): T[][] {
  const groups: T[][] = [];
  let current: T[] = [];

  rows.forEach((row, index) => {
    if (index > 0 && row.connector === 'or' && current.length > 0) {
      groups.push(current);
      current = [];
    }
    current.push(row);
  });

  if (current.length > 0) {
    groups.push(current);
  }
  return groups;
}

export type EditableFilterRow = {
  id: string;
  field: string;
  operator: string;
  value: string | string[];
  connector: FilterConnector;
};

/** The single text value of a row. A list value, which only list fields hold, reads as empty. */
export function textValueOf(row: Pick<EditableFilterRow, 'value'>): string {
  return Array.isArray(row.value) ? '' : row.value;
}

type FilterTreeClause<TClause> = {
  and?: TClause[] | null;
  or?: TClause[] | null;
};

type FilterTreeGroup<TClause> = TClause | { and?: TClause[] };

type FilterTree<TClause> =
  FilterTreeGroup<TClause> | { or?: FilterTreeGroup<TClause>[] };

/**
 * Turns filter rows into one GraphQL filter: rows in an `and` group become an `and`
 * clause, and the groups are joined by `or`. Rows `rowToClause` rejects are left out.
 */
export function buildFilterTree<
  TRow extends { connector: FilterConnector },
  TClause extends FilterTreeClause<TClause>,
>(
  rows: TRow[],
  rowToClause: (row: TRow) => TClause | null
): FilterTree<TClause> | undefined {
  const groups: FilterTreeGroup<TClause>[] = groupRows(rows)
    .map((group) =>
      group
        .map(rowToClause)
        .filter((clause): clause is TClause => clause !== null)
    )
    .filter((clauses) => clauses.length > 0)
    .map((clauses) =>
      clauses.length === 1 ? clauses[0] : buildAndFilter(clauses)
    );

  if (groups.length === 0) return undefined;
  return groups.length === 1 ? groups[0] : buildOrFilter(groups);
}

export type SavedFilterRow = {
  field: string;
  operator: string;
  value: string | string[];
  connector?: string;
};

export type SavedFilterRowSchema = {
  operatorsForField: (field: string) => readonly string[] | null;
  isListField: (field: string) => boolean;
};

export function filterRowsToSaved(rows: EditableFilterRow[]): SavedFilterRow[] {
  return rows.map((row) => ({
    field: row.field,
    operator: row.operator,
    value: row.value,
    connector: row.connector,
  }));
}

function isSavedFilterRow(entry: unknown): entry is SavedFilterRow {
  if (typeof entry !== 'object' || entry === null) return false;
  const candidate = entry as Record<string, unknown>;
  const hasUsableValue =
    typeof candidate.value === 'string' ||
    (Array.isArray(candidate.value) &&
      candidate.value.every((item) => typeof item === 'string'));
  return (
    typeof candidate.field === 'string' &&
    typeof candidate.operator === 'string' &&
    hasUsableValue
  );
}

function savedValueFor(
  entry: SavedFilterRow,
  schema: SavedFilterRowSchema
): string | string[] {
  if (schema.isListField(entry.field)) {
    return Array.isArray(entry.value) ? entry.value : [entry.value];
  }
  return Array.isArray(entry.value) ? (entry.value[0] ?? '') : entry.value;
}

/**
 * Reads stored filter rows back into editable rows, skipping any whose field or
 * operator the page does not know.
 */
export function readSavedFilterRows(
  raw: unknown,
  schema: SavedFilterRowSchema
): EditableFilterRow[] {
  if (!Array.isArray(raw)) return [];

  const rows: EditableFilterRow[] = [];
  for (const entry of raw) {
    if (!isSavedFilterRow(entry)) continue;
    const operators = schema.operatorsForField(entry.field);
    if (operators === null || !operators.includes(entry.operator)) continue;
    rows.push({
      id: `saved-${rows.length}-${entry.field}-${entry.operator}`,
      field: entry.field,
      operator: entry.operator,
      value: savedValueFor(entry, schema),
      connector: entry.connector === 'or' ? 'or' : 'and',
    });
  }
  return rows;
}
